
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Code, Lightbulb, Scale, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Badge } from "@/components/ui/badge";
import EditAssistantDialog from "../assistant/EditAssistantDialog";
import { getOpenAIClient } from "@/lib/openai";

interface Assistant {
  id: string;
  name: string;
  assistant_id: string;
  model: string;
  creativity: string;
}

interface AssistantsListProps {
  refreshTrigger?: number;
  onCountChange?: (count: number) => void;
}

export const AssistantsList = ({ refreshTrigger = 0, onCountChange }: AssistantsListProps) => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [editingAssistantId, setEditingAssistantId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("Fetching assistants, trigger:", refreshTrigger);
    fetchAssistants();
  }, [refreshTrigger]);

  const fetchAssistants = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('assistants')
        .select('*');

      if (error) throw error;
      if (data) {
        console.log("Fetched assistants:", data);
        setAssistants(data);
        if (onCountChange) {
          onCountChange(data.length);
        }
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
      toast.error("Failed to load assistants. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAssistant = async (id: string, assistantId: string) => {
    try {
      setIsDeleting(true);
      console.log('Deleting assistant:', { id, assistantId });

      // Delete from OpenAI first
      const openai = await getOpenAIClient();
      await openai.beta.assistants.del(assistantId);
      console.log('Assistant deleted from OpenAI');

      // Then delete from our database
      const { error } = await supabase
        .from('assistants')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAssistants(assistants.filter((a) => a.id !== id));
      if (onCountChange) {
        onCountChange(assistants.length - 1);
      }
      toast.success("Assistant removed successfully.");
    } catch (error) {
      console.error('Error deleting assistant:', error);
      toast.error("Failed to delete assistant. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const getModelIcon = (model: string = 'gpt-4o-mini') => {
    return <Code className="h-4 w-4" />;
  };

  const getCreativityIcon = (creativity: string = 'neutral') => {
    switch (creativity) {
      case 'technical':
        return <Code className="h-4 w-4" />;
      case 'creative':
        return <Lightbulb className="h-4 w-4" />;
      default:
        return <Scale className="h-4 w-4" />;
    }
  };

  const formatCreativity = (creativity: string = 'neutral') => {
    return creativity.charAt(0).toUpperCase() + creativity.slice(1);
  };

  const handleEditComplete = () => {
    setEditingAssistantId(null);
    fetchAssistants();
  };

  const handleRefresh = () => {
    fetchAssistants();
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-left">Current Assistants</h3>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-sm text-muted-foreground p-4 border rounded-lg text-center">
            Loading assistants...
          </div>
        ) : assistants.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 border rounded-lg text-center">
            No assistants found. Create one using the button below.
          </div>
        ) : (
          assistants.map((assistant) => (
            <div
              key={assistant.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="space-y-2">
                <h3 className="font-medium text-left">{assistant.name}</h3>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {getModelIcon(assistant.model)}
                    {assistant.model === 'gpt-4o' ? 'Extended' : 'Efficient'}
                  </Badge>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {getCreativityIcon(assistant.creativity)}
                    {formatCreativity(assistant.creativity)}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingAssistantId(assistant.id)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => handleDeleteAssistant(assistant.id, assistant.assistant_id)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!editingAssistantId} onOpenChange={() => setEditingAssistantId(null)}>
        {editingAssistantId && (
          <EditAssistantDialog
            assistantId={editingAssistantId}
            onSave={handleEditComplete}
          />
        )}
      </Dialog>
    </>
  );
};
